import api from '../config/api';
import { isApiEnabled } from '../config/appConfig';
import { getApiData, parseApiError } from './apiUtils';

export const fetchAgendaSpecialties = () => {
  if (!isApiEnabled()) {
    return Promise.reject(new Error('La agenda requiere conexión con el servidor.'));
  }

  return api
    .post('/agenda_especialidades.php', {})
    .then((response) => getApiData(response, 'No se pudieron cargar las especialidades.'))
    .then((data) => data.especialidades || [])
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const fetchAgendaSpecialists = () => {
  if (!isApiEnabled()) {
    return Promise.reject(new Error('La agenda requiere conexión con el servidor.'));
  }

  return api
    .post('/agenda_especialistas.php', {})
    .then((response) => getApiData(response, 'No se pudieron cargar los especialistas.'))
    .then((data) => data.especialistas || [])
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const fetchAgendaSpecialistsPresencial = (sede, idespecialidad) => {
  if (!sede) {
    return Promise.reject(new Error('Indica la sede para consultar especialistas.'));
  }

  if (!idespecialidad) {
    return Promise.reject(new Error('Indica la especialidad para consultar especialistas.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La agenda requiere conexión con el servidor.'));
  }

  return api
    .post('/agenda_especialistas_presencial.php', {
      sede,
      idespecialidad,
    })
    .then((response) => getApiData(response, 'No se pudieron cargar los especialistas de Terapia.'))
    .then((data) => data.especialistas || [])
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const fetchAgendaSedes = (pais) => {
  if (!pais) {
    return Promise.reject(new Error('Indica el país para consultar sedes.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La agenda requiere conexión con el servidor.'));
  }

  return api
    .post('/agenda_sedes.php', { pais })
    .then((response) => getApiData(response, 'No se pudieron cargar las sedes.'))
    .then((data) => data.sedes || [])
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const fetchAvailability = (payload) => {
  const options = typeof payload === 'object' && payload !== null ? payload : { especialistaId: payload };

  if (!options.especialistaId && !options.terapiaDocumento) {
    return Promise.reject(new Error('Especialista no válido.'));
  }

  if (!isApiEnabled()) {
    return Promise.reject(new Error('La agenda requiere conexión con el servidor.'));
  }

  const body = {
    fecha_inicio: options.fechaInicio,
    fecha_fin: options.fechaFin,
  };

  if (options.modalidad === 'presencial' || options.terapiaDocumento) {
    body.modalidad = 'presencial';
    body.terapia_documento = options.terapiaDocumento;
    body.sede = options.sede || '';
    body.especialista_nombre = options.especialistaNombre || '';
  } else {
    body.especialista_id = options.especialistaId;
  }

  if (options.solicitanteId) {
    body.solicitante_id = options.solicitanteId;
  }

  if (options.pacienteFichaId) {
    body.paciente_ficha_id = Number(options.pacienteFichaId);
  }

  return api
    // Nombre distinto de informes_api para evitar sobrescrituras accidentales en el hosting.
    .post('/agenda_calendario_api.php', body)
    .then((response) => getApiData(response, 'No se pudo consultar la disponibilidad.'))
    .catch((error) => {
      throw parseApiError(error);
    });
};

const postCita = (path, body, fallbackMessage) => {
  if (!isApiEnabled()) {
    return Promise.reject(new Error('La agenda requiere conexión con el servidor.'));
  }

  return api
    .post(path, body)
    .then((response) => getApiData(response, fallbackMessage))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const confirmAgendaCita = (payload) =>
  postCita('/agenda_cita_confirmar.php', payload, 'No se pudo confirmar la cita.');

export const payAgendaCita = (payload) =>
  postCita('/agenda_cita_pagar.php', payload, 'No se pudo iniciar el pago.');

export const registerAgendaPayment = (payload) =>
  postCita('/agenda_cita_registrar_pago.php', payload, 'No se pudo registrar el pago.');

export const confirmStripeReturn = (sessionId) =>
  postCita(
    '/agenda_cita_stripe_regreso.php',
    { session_id: sessionId },
    'No se pudo confirmar el pago.'
  );

export const fetchAgendaReceipt = (payload) =>
  postCita('/agenda_cita_recibo.php', payload, 'No se pudo abrir el recibo.');

export const cancelAgendaCita = (payload) =>
  postCita('/agenda_cita_cancelar.php', payload, 'No se pudo cancelar la cita.');

export const rescheduleAgendaCita = (payload) =>
  postCita('/agenda_cita_cambiar_fecha.php', payload, 'No se pudo cambiar la fecha.');

export const ensureAgendaTeamsLink = (payload) =>
  postCita('/agenda_cita_teams.php', payload, 'No se pudo gestionar el enlace de Teams.');

export const fetchAgendaChat = (payload) =>
  postCita(
    '/agenda_chat.php',
    { ...(payload || {}), accion: 'listar' },
    'No se pudieron cargar los mensajes.'
  );

export const fetchAgendaPacienteFicha = (payload) =>
  postCita('/agenda_paciente_ficha.php', payload, 'No se pudo abrir la ficha del paciente.');

export const sendAgendaChatMessage = (payload) =>
  postCita(
    '/agenda_chat.php',
    { ...(payload || {}), accion: 'enviar' },
    'No se pudo enviar el mensaje.'
  );

export const isPastAgendaDate = (fecha) => {
  if (!fecha) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const parts = String(fecha).split('-');

  if (parts.length !== 3) {
    return false;
  }

  const day = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  day.setHours(0, 0, 0, 0);

  return day < today;
};

/** Día anterior, o hoy con hora ya cumplida (la hora en curso sigue abierta). */
export const isPastAgendaSlot = (fecha, hora) => {
  if (!fecha) {
    return false;
  }

  if (isPastAgendaDate(fecha)) {
    return true;
  }

  const today = formatDateYMD(new Date());

  if (String(fecha).slice(0, 10) !== today) {
    return false;
  }

  const match = String(hora || '').match(/^(\d{2}):(\d{2})/);

  if (!match) {
    return true;
  }

  const ahora = new Date();
  const horaActual = `${String(ahora.getHours()).padStart(2, '0')}:00`;

  return `${match[1]}:${match[2]}` < horaActual;
};

/** Solo la hora en curso del día de hoy coincide con el slot (14:00–14:59 → 14:00). */
export const isAgendaSlotHoraActual = (fecha, hora) => {
  if (!fecha) {
    return false;
  }

  const today = formatDateYMD(new Date());

  if (String(fecha).slice(0, 10) !== today) {
    return false;
  }

  const match = String(hora || '').match(/^(\d{2})/);

  if (!match) {
    return false;
  }

  const horaActual = String(new Date().getHours()).padStart(2, '0');

  return match[1] === horaActual;
};

const horaSlotDosDigitos = (hora) => {
  const match = String(hora || '').match(/^(\d{2})/);
  return match ? match[1] : '';
};

const horaActualDosDigitos = () => String(new Date().getHours()).padStart(2, '0');

/** La hora actual ya superó la de la cita (ayer, o hoy con hora mayor). */
export const isAgendaSlotHoraSuperada = (fecha, hora) => {
  if (!fecha) {
    return false;
  }

  if (isPastAgendaDate(fecha)) {
    return true;
  }

  const today = formatDateYMD(new Date());

  if (String(fecha).slice(0, 10) !== today) {
    return false;
  }

  const horaSlot = horaSlotDosDigitos(hora);

  if (!horaSlot) {
    return false;
  }

  return horaActualDosDigitos() > horaSlot;
};

/** Reenviar solo si la cita es futura: otro día, o hoy con hora mayor a la actual. */
export const isAgendaSlotReenviarDisponible = (fecha, hora) => {
  if (!fecha) {
    return false;
  }

  if (isPastAgendaDate(fecha)) {
    return false;
  }

  const today = formatDateYMD(new Date());

  if (String(fecha).slice(0, 10) !== today) {
    return true;
  }

  const horaSlot = horaSlotDosDigitos(hora);

  if (!horaSlot) {
    return false;
  }

  return horaSlot > horaActualDosDigitos();
};

export const fetchInventarioPrecio = (especialidadCodigo, tipoCita) => {
  if (!especialidadCodigo || !tipoCita) {
    return Promise.resolve(null);
  }

  if (!isApiEnabled()) {
    return Promise.resolve(null);
  }

  return api
    .post('/inventario_precio.php', {
      especialidad_codigo: String(especialidadCodigo),
      tipo_cita: tipoCita,
    })
    .then((response) => getApiData(response, 'No se pudo consultar el precio.'))
    .then((data) => (data && data.servicio ? data.servicio : null))
    .catch(() => null);
};

export const fetchEspecialistaServicios = ({
  especialistaId,
  terapiaDocumento,
  especialidadCodigo,
  tipoCita,
} = {}) => {
  if (!isApiEnabled()) {
    return Promise.resolve([]);
  }

  const body = {
    especialista_id: especialistaId ? Number(especialistaId) : 0,
    terapia_documento: terapiaDocumento ? String(terapiaDocumento) : '',
    especialidad_codigo: especialidadCodigo ? String(especialidadCodigo) : '',
  };

  if (tipoCita) {
    body.tipo_cita = tipoCita;
  }

  return api
    .post('/agenda_especialista_servicios.php', body)
    .then((response) => getApiData(response, 'No se pudieron cargar los servicios.'))
    .then((data) => (data && Array.isArray(data.servicios) ? data.servicios : []))
    .catch((error) => {
      throw parseApiError(error);
    });
};

export const formatAgendaPrecio = (precio, moneda) => {
  if (precio === null || precio === undefined || precio === '') {
    return '';
  }

  const amount = Number(precio);

  if (Number.isNaN(amount)) {
    return '';
  }

  return `${moneda || 'PAB'} ${amount.toFixed(2)}`;
};

export const MENSAJE_PAGO_24H =
  'Tienes un lapso de 24 horas para realizar el pago. Si no lo haces, la cita se eliminará.';

export const agendaCitaTipoLabel = (tipo) => {
  if (tipo === 'evaluacion') {
    return 'Evaluación';
  }

  if (tipo === 'terapia') {
    return 'Terapia';
  }

  return 'Cita';
};

const pad = (value) => String(value).padStart(2, '0');

export const formatDateYMD = (date) => {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const dayLabels = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const monthLabels = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export const getWeekRange = (weekOffset = 0) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7) + weekOffset * 7);

  const days = [];

  for (let index = 0; index < 6; index += 1) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);

    days.push({
      key: formatDateYMD(date),
      date: formatDateYMD(date),
      dayNumber: date.getDate(),
      label: dayLabels[date.getDay()],
      monthLabel: monthLabels[date.getMonth()],
      isoWeekday: date.getDay() === 0 ? 7 : date.getDay(),
    });
  }

  return {
    start: days[0].date,
    end: days[days.length - 1].date,
    days,
  };
};

export const formatSlotLabel = (fecha, hora) => {
  const parts = fecha.split('-');
  if (parts.length !== 3) {
    return `${fecha} ${hora}`;
  }

  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const dayName = dayLabels[date.getDay()];
  const monthName = monthLabels[date.getMonth()];

  return `${dayName}, ${date.getDate()} de ${monthName} · ${hora}`;
};

export const CALENDAR_HOURS = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00',
];

export const buildSlotMap = (slots) => {
  const map = {};

  if (!Array.isArray(slots)) {
    return map;
  }

  slots.forEach((slot) => {
    map[`${slot.fecha}|${slot.hora}`] = slot;
  });

  return map;
};

const DEFAULT_SPECIALTY_IMAGE = 'service-terapias.png';

const specialtyImagesByCodigo = {
  1: 'service-fonoaudiologia.png',
  2: 'service-terapia-ocupacional.png',
  4: 'service-psicologia.png',
  8: 'service-integracion-sensorial.png',
  10: 'service-estimulacion-temprana.png',
};

/** Fallback por nombre cuando el código no está mapeado (p. ej. Medicina General). */
const specialtyImagesByKeyword = [
  { match: /fonoaudiolog/, image: 'service-fonoaudiologia.png' },
  { match: /ocupacional/, image: 'service-terapia-ocupacional.png' },
  { match: /psicolog/, image: 'service-psicologia.png' },
  { match: /sensorial/, image: 'service-integracion-sensorial.png' },
  { match: /estimulacion/, image: 'service-estimulacion-temprana.png' },
  { match: /medicina/, image: 'service-terapia-familiar.png' },
  { match: /fisioterap/, image: 'about-service-intervencion.png' },
  { match: /pedagog/, image: 'about-service-tutorias.png' },
  { match: /familiar/, image: 'service-terapia-familiar.png' },
  { match: /conducta/, image: 'service-modificacion-conducta.png' },
];

const stripDiacritics = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const resolveSpecialtyImage = (codigo, label) => {
  const fromCodigo = specialtyImagesByCodigo[codigo];
  if (fromCodigo) {
    return fromCodigo;
  }

  const normalizedLabel = stripDiacritics(label).toLowerCase();
  const byKeyword = specialtyImagesByKeyword.find((item) => item.match.test(normalizedLabel));
  if (byKeyword) {
    return byKeyword.image;
  }

  return DEFAULT_SPECIALTY_IMAGE;
};

/** Correcciones de tilde para etiquetas que llegan sin acento desde Terapias. */
const specialtyAccentWords = {
  psicologo: 'Psicólogo',
  psicologa: 'Psicóloga',
  psicologia: 'Psicología',
  fonoaudiologia: 'Fonoaudiología',
  fonoaudiologo: 'Fonoaudiólogo',
  fonoaudiologa: 'Fonoaudióloga',
  estimulacion: 'Estimulación',
  pedagogia: 'Pedagogía',
  pedagogo: 'Pedagogo',
  pedagoga: 'Pedagoga',
  integracion: 'Integración',
  fisioterapia: 'Fisioterapia',
  neurologia: 'Neurología',
  neurologo: 'Neurólogo',
  neurologa: 'Neuróloga',
  logopedia: 'Logopedia',
  ortopedia: 'Ortopedia',
  optometria: 'Optometría',
  nutricion: 'Nutrición',
  nutricionista: 'Nutricionista',
  educacion: 'Educación',
};

export const formatSpecialtyLabel = (label) => {
  if (!label) {
    return '';
  }

  return String(label)
    .split(/[\s,/]+/)
    .filter(Boolean)
    .map((word) => {
      const key = stripDiacritics(word).toLowerCase();
      if (specialtyAccentWords[key]) {
        return specialtyAccentWords[key];
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

export const enrichSpecialty = (specialty) => {
  const codigo = String(specialty.codigo || specialty.id || '');
  const label = formatSpecialtyLabel(specialty.label);

  return {
    id: codigo,
    codigo,
    label,
    labelRaw: specialty.label,
    image: resolveSpecialtyImage(codigo, label || specialty.label),
    badge: 'TERAPIA',
  };
};

export const specialistMatchesSpecialty = (specialist, specialtyCodigo) => {
  if (!specialtyCodigo) {
    return true;
  }

  const codigo = String(specialtyCodigo);

  const casos = Array.isArray(specialist.casos_codigos) ? specialist.casos_codigos : [];

  if (casos.length === 0) {
    return false;
  }

  return casos.some((item) => String(item) === codigo);
};

export const filterSpecialistsBySpecialty = (specialists, specialtyCodigo) => {
  if (!Array.isArray(specialists)) {
    return [];
  }

  return specialists.filter((specialist) => specialistMatchesSpecialty(specialist, specialtyCodigo));
};

const normalizeSede = (value) => {
  const text = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/Á/g, 'A')
    .replace(/É/g, 'E')
    .replace(/Í/g, 'I')
    .replace(/Ó/g, 'O')
    .replace(/Ú/g, 'U')
    .replace(/Ü/g, 'U')
    .replace(/Ñ/g, 'N');

  return text.replace(/\s+/g, ' ');
};

export const specialistMatchesSede = (specialist, sedeLabel) => {
  if (!sedeLabel) {
    return true;
  }

  const target = normalizeSede(sedeLabel);

  if (!target) {
    return true;
  }

  const sedes = Array.isArray(specialist.sedes_terapias) ? specialist.sedes_terapias : [];

  if (sedes.length === 0) {
    return false;
  }

  return sedes.some((sede) => normalizeSede(sede) === target || normalizeSede(sede).includes(target));
};

export const filterSpecialistsBySpecialtyAndSede = (specialists, specialtyCodigo, sedeLabel) => {
  return filterSpecialistsBySpecialty(specialists, specialtyCodigo).filter((specialist) =>
    specialistMatchesSede(specialist, sedeLabel)
  );
};
